export type ApiError = { status?: string; code?: string; title?: string; detail?: string };
export type ApiEnvelopeSingle<T> = { data: T; meta?: any; links?: Record<string,string>; errors?: ApiError[] };
export type ApiEnvelopePaged<T> = { data: T[]; meta?: { total?: number; page?: number; size?: number; timestamp?: string }; links?: Record<string,string>; errors?: ApiError[] };
export interface ApiViolation {
  message: string;
  path?: string;
  invalidValue?: unknown;
}

export interface ExtractedApiError {
  message: string;
  fieldErrors?: Record<string, string>;
}

export function extractApiError(error: any): ExtractedApiError {
    const backend = error?.response?.data;

    // JSON:API style errors array (e.g. { errors: [{ detail, title, code, status }] })
    if (Array.isArray(backend?.errors) && backend.errors.length) {
      const fieldErrors: Record<string,string> = {};
      const messages = backend.errors.map((e: ApiError) => {
        // prefer detailed human message
        return e.detail ?? e.title ?? e.code ?? JSON.stringify(e);
      }).join('\n');
      return { message: messages, fieldErrors: Object.keys(fieldErrors).length ? fieldErrors : undefined };
    }

    // Validation errors from backend (violations)
    if (Array.isArray(backend?.violations)) {
      const fieldErrors: Record<string, string> = {};

      backend.violations.forEach((v: ApiViolation) => {
        if (v.path) fieldErrors[v.path] = v.message;
      });

      return {
        message: backend.violations.map((v: ApiViolation) => v.message).join("\n"),
        fieldErrors
      };
    }

    // Backend message
    if (backend?.message) {
      return { message: backend.message };
    }

    // Axios generic error
    return { message: error.message ?? "Unknown error" };
  }

function throwIfErrors(envelope: any) {
  // Standard JSON:API errors
  if (Array.isArray(envelope?.errors) && envelope.errors.length) {
    const e = envelope.errors[0];
    const msg = e.detail ?? e.title ?? JSON.stringify(e);
    throw new Error(msg);
  }

  // Your backend validation errors
  if (Array.isArray(envelope?.violations)) {
    const messages = envelope.violations
      .map((v: { message: string }) => v.message)
      .join("\n");
    throw new Error(messages);
  }


  // Generic error message
  if (envelope?.error || envelope?.message) {
    throw new Error(envelope.message ?? envelope.error);
  }
}


export function unwrapListResponse<T>(envelope: ApiEnvelopePaged<T> | any): T[] {
  throwIfErrors(envelope);
  if (!envelope) return [];
  const payload = envelope.data;
  if (!payload) return [];
  return Array.isArray(payload) ? payload : [payload];
}

export function unwrapMetaResponse<T>(envelope: ApiEnvelopeSingle<T> | any): T | null {
  throwIfErrors(envelope);
  if (!envelope || envelope.meta === undefined) return null;
  return envelope.meta as T;
}

export function unwrapSingleResponse<T>(envelope: ApiEnvelopeSingle<T> | any): T | null {
  throwIfErrors(envelope);
  if (!envelope || envelope.data === undefined) return null;
  return envelope.data as T;
}